// Analytics and tracking utilities for feature flags
import { FeatureFlags } from '../generated/feature-flags';

// Global declarations for analytics services
declare global {
    var gtag: any;
    var appInsights: any;
}

export interface FeatureFlagAnalyticsEvent {
    flagName: string;
    enabled: boolean;
    userContext?: Record<string, any>;
    timestamp: string;
    sessionId?: string;
    userId?: string;
}

export class FeatureFlagAnalytics {
    private static instance: FeatureFlagAnalytics;
    private events: FeatureFlagAnalyticsEvent[] = [];
    private sessionId: string;
    private userId?: string;

    private constructor() {
        this.sessionId = this.generateSessionId();
    }

    static getInstance(): FeatureFlagAnalytics {
        if (!FeatureFlagAnalytics.instance) {
            FeatureFlagAnalytics.instance = new FeatureFlagAnalytics();
        }
        return FeatureFlagAnalytics.instance;
    }

    setUserId(userId: string): void {
        this.userId = userId;
    }

    trackFeatureFlagUsage(flagName: keyof FeatureFlags, enabled: boolean, userContext?: Record<string, any>): void {
        const event: FeatureFlagAnalyticsEvent = {
            flagName: flagName as string,
            enabled,
            userContext,
            timestamp: new Date().toISOString(),
            sessionId: this.sessionId,
            userId: this.userId
        };

        this.events.push(event);

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.log('🚩 Feature Flag Used:', event);
        }

        // Send to analytics service (implement based on your analytics provider)
        this.sendToAnalytics(event);
    }

    getUsageStats(): Record<string, { used: number; enabled: number; disabled: number }> {
        const stats: Record<string, { used: number; enabled: number; disabled: number }> = {};

        this.events.forEach((event) => {
            if (!stats[event.flagName]) {
                stats[event.flagName] = { used: 0, enabled: 0, disabled: 0 };
            }

            stats[event.flagName].used++;
            if (event.enabled) {
                stats[event.flagName].enabled++;
            } else {
                stats[event.flagName].disabled++;
            }
        });

        return stats;
    }

    getRecentEvents(limit: number = 50): FeatureFlagAnalyticsEvent[] {
        return this.events.slice(-limit);
    }

    clearEvents(): void {
        this.events = [];
    }

    private generateSessionId(): string {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    private async sendToAnalytics(event: FeatureFlagAnalyticsEvent): Promise<void> {
        try {
            // Example: Send to Google Analytics
            if (typeof gtag !== 'undefined') {
                gtag('event', 'feature_flag_usage', {
                    custom_parameter_flag_name: event.flagName,
                    custom_parameter_enabled: event.enabled,
                    custom_parameter_session_id: event.sessionId
                });
            }

            // Example: Send to custom analytics endpoint
            if (process.env.ANALYTICS_ENDPOINT) {
                await fetch(process.env.ANALYTICS_ENDPOINT, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        type: 'feature_flag_usage',
                        data: event
                    })
                });
            }

            // Example: Send to Application Insights
            if (typeof appInsights !== 'undefined') {
                appInsights.trackEvent({
                    name: 'FeatureFlagUsage',
                    properties: {
                        flagName: event.flagName,
                        enabled: event.enabled.toString(),
                        sessionId: event.sessionId,
                        userId: event.userId || 'anonymous'
                    }
                });
            }
        } catch (error) {
            console.warn('Failed to send feature flag analytics:', error);
        }
    }
}

// Hook for tracking feature flag usage
export function useFeatureFlagAnalytics() {
    const analytics = FeatureFlagAnalytics.getInstance();
    const trackUsage = (flagName: keyof FeatureFlags, enabled: boolean, userContext?: Record<string, any>) => {
        analytics.trackFeatureFlagUsage(flagName, enabled, userContext);
    };
    return {
        trackUsage,
        getUsageStats: () => analytics.getUsageStats(),
        getRecentEvents: (limit?: number) => analytics.getRecentEvents(limit),
        setUserId: (userId: string) => analytics.setUserId(userId)
    };
}

// Performance monitoring for feature flags
export class FeatureFlagPerformanceMonitor {
    private static performanceMarks: Map<string, number> = new Map();

    static startMeasurement(flagName: string): void {
        const markName = `feature_flag_${flagName}_start`;
        if (typeof performance !== 'undefined') {
            performance.mark(markName);
            this.performanceMarks.set(flagName, performance.now());
        }
    }

    static endMeasurement(flagName: string): number | null {
        if (typeof performance === 'undefined') return null;

        const startTime = this.performanceMarks.get(flagName);
        if (!startTime) return null;

        const endTime = performance.now();
        const duration = endTime - startTime;

        const markName = `feature_flag_${flagName}_end`;
        performance.mark(markName);

        try {
            performance.measure(`feature_flag_${flagName}_duration`, `feature_flag_${flagName}_start`, markName);
        } catch (error) {
            console.warn('Performance measurement failed:', error);
        }

        this.performanceMarks.delete(flagName);

        // Log slow feature flag evaluations
        if (duration > 100) {
            // 100ms threshold
            console.warn(`Slow feature flag evaluation: ${flagName} took ${duration.toFixed(2)}ms`);
        }

        return duration;
    }

    static getPerformanceEntries(): PerformanceEntry[] {
        if (typeof performance === 'undefined') return [];

        return performance.getEntriesByType('measure').filter((entry) => entry.name.startsWith('feature_flag_'));
    }
}
